import React, { useState, useEffect } from 'react';
import { safeLocalStorage } from '@/lib/storage-utils';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Settings,
  X,
  Type,
  Palette,
  Image,
  Upload,
  Save,
  RotateCcw,
  Trash2,
  Info,
  Globe
} from 'lucide-react';
import { useBrand } from '@/context/BrandContext';
import { ImageUploadModal } from '../image-upload';
import { TypographyDropdown } from '../ui/TypographyDropdown';
import { colorPresetService } from '@/lib/colorPresetService';
import { ColorPreset } from '../color-presets/types';
import { useWorkspace } from '@/context/WorkspaceContext';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';

interface BrandSettingsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  isGlobalDarkMode: boolean;
}

interface CustomFont {
  id: string;
  name: string;
  file: File;
  url: string;
  format: string;
}

export const BrandSettingsPanel: React.FC<BrandSettingsPanelProps> = ({
  isOpen,
  onClose,
  isGlobalDarkMode
}) => {
  const { state, dispatch } = useBrand();
  const { currentWorkspace } = useWorkspace();
  const [activeTab, setActiveTab] = useState<'general' | 'typography' | 'colors'>('general');

  // Local state for settings
  const [settings, setSettings] = useState({
    brandName: '',
    brandLogo: '',
    brandThumbnail: '',
    globalTitleFont: 'Helvetica',
    globalTextFont: 'Helvetica',
    globalTitleFontColor: 'transparent',
    globalTextFontColor: 'transparent',
    primaryColor: '#3B82F6',
    secondaryColor: '#8B5CF6',
    accentColor: '#F59E0B',
    bgColor: '#FFFFFF',
    titleColor: '#000000',
    textColor: '#374151',
    selectedColorPreset: null as ColorPreset | null,
    designCredit: currentWorkspace?.name || ''
  });

  // Custom fonts state
  const [customFonts, setCustomFonts] = useState<CustomFont[]>([]);
  const [showImageModal, setShowImageModal] = useState(false);
  const [currentImageField, setCurrentImageField] = useState<'brandLogo' | 'brandThumbnail' | 'primaryDarkLogo' | null>(null);

  // Color presets state
  const [colorPresets, setColorPresets] = useState<ColorPreset[]>([]);
  const [isLoadingColorPresets, setIsLoadingColorPresets] = useState(true);

  // Load color presets
  useEffect(() => {
    const loadColorPresets = async () => {
      if (!currentWorkspace) return;
      setIsLoadingColorPresets(true);
      try {
        const presets = await colorPresetService.getPresetsByWorkspace(currentWorkspace.id);
        setColorPresets(presets);
      } catch (error) {
        console.error('Failed to load color presets:', error);
        toast.error('Failed to load color presets');
        setColorPresets([]);
      } finally {
        setIsLoadingColorPresets(false);
      }
    };
    loadColorPresets();
  }, [currentWorkspace]);

  // Initialize settings from brand data
  useEffect(() => {
    if (state.brandData) {
      setSettings({
        brandName: state.brandData.name || '',
        brandLogo: state.brandData.brandLogo || '',
        brandThumbnail: state.brandData.brandThumbnail || '',
        globalTitleFont: state.brandData.globalTitleFont || 'Helvetica',
        globalTextFont: state.brandData.globalTextFont || 'Helvetica',
        globalTitleFontColor: state.brandData.globalTitleFontColor || 'transparent',
        globalTextFontColor: state.brandData.globalTextFontColor || 'transparent',
        primaryColor: state.brandData.colors?.primary || '#3B82F6',
        secondaryColor: state.brandData.colors?.secondary ?? '',
        accentColor: state.brandData.colors?.accent ?? '',
        bgColor: state.brandData.colors?.background || '#FFFFFF',
        titleColor: state.brandData.colors?.title || '#000000',
        textColor: state.brandData.colors?.text ?? '',
        selectedColorPreset: state.brandData.selectedColorPreset || null,
        designCredit: state.brandData.designCredit || currentWorkspace?.name || ''
      });
    }
  }, [state.brandData, isOpen]);

  // Load custom fonts from localStorage
  useEffect(() => {
    const savedCustomFonts = safeLocalStorage.getItem('customFonts');
    if (savedCustomFonts) {
      try {
        const parsed = JSON.parse(savedCustomFonts);
        setCustomFonts(parsed);
      } catch (error) {
        console.error('Failed to load custom fonts:', error);
      }
    }
  }, []);

  // Tab navigation
  const handleTabChange = (tab: 'general' | 'typography' | 'colors') => {
    setActiveTab(tab);
  };

  // Update setting
  const updateSetting = (key: string, value: string) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Handle image upload
  const handleImageUpload = (field: 'brandLogo' | 'brandThumbnail' | 'primaryDarkLogo') => {
    setCurrentImageField(field);
    setShowImageModal(true);
  };

  const handleImageSelect = (imageUrl: string, _imageData?: any) => {
    if (currentImageField) {
      if (currentImageField === 'primaryDarkLogo') {
        if (!state.brandData) return;
        // Update the brand data directly for dark logo
        dispatch({
          type: 'SET_BRAND_DATA',
          payload: {
            ...state.brandData,
            primaryDarkLogo: imageUrl
          }
        });
      } else {
        // Update local settings for other fields
        updateSetting(currentImageField, imageUrl);
      }
    }
    setShowImageModal(false);
    setCurrentImageField(null);
  };

  // Handle custom font upload
  const handleCustomFontUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    const allowedTypes = ['.ttf', '.otf', '.woff', '.woff2'];
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();

    if (!allowedTypes.includes(fileExtension)) {
      alert('Please upload a valid font file (.ttf, .otf, .woff, .woff2)');
      return;
    }

    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      alert('Font file size must be less than 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;
      const fontName = file.name.replace(/\.[^/.]+$/, "");
      
      const customFont: CustomFont = {
        id: Date.now().toString(),
        name: fontName,
        file: file,
        url: result,
        format: fileExtension.substring(1)
      };

      // Create font face
      const fontFace = new FontFace(fontName, `url(${result})`);
      fontFace.load().then(() => {
        document.fonts.add(fontFace);
        
        const updatedCustomFonts = [...customFonts, customFont];
        setCustomFonts(updatedCustomFonts);
        safeLocalStorage.setItem('customFonts', JSON.stringify(updatedCustomFonts));
      }).catch((error) => {
        console.error('Failed to load custom font:', error);
        alert('Failed to load the font file. Please try a different file.');
      });
    };

    reader.readAsDataURL(file);
    
    // Reset file input
    event.target.value = '';
  };

  // Remove custom font
  const removeCustomFont = (fontId: string) => {
    const updatedCustomFonts = customFonts.filter(font => font.id !== fontId);
    setCustomFonts(updatedCustomFonts);
    localStorage.setItem('customFonts', JSON.stringify(updatedCustomFonts));
  };

  // Remove logo/thumbnail functions
  const removeBrandLogo = () => {
    setSettings(prev => ({ ...prev, brandLogo: '' }));
  };

  const removeDarkLogo = () => {
    if (!state.brandData) return;
    dispatch({
      type: 'SET_BRAND_DATA',
      payload: {
        ...state.brandData,
        primaryDarkLogo: ''
      }
    });
  };

  const removeBrandThumbnail = () => {
    setSettings(prev => ({ ...prev, brandThumbnail: '' }));
  };



  // Apply general settings (brand identity, logos, etc.)
  const applyGeneralSettings = () => {
    if (!state.brandData) return;
    // Update brand data
    const updatedBrandData = {
      ...state.brandData,
      name: settings.brandName,
      brandLogo: settings.brandLogo,
      brandThumbnail: settings.brandThumbnail,
      designCredit: settings.designCredit,
      // Keep the primaryDarkLogo from the current state since it's managed separately
      primaryDarkLogo: state.brandData?.primaryDarkLogo || ''
    };

    dispatch({ type: 'SET_BRAND_DATA', payload: updatedBrandData });

    // Update sections with brand name and logo
    const updatedSections = state.sections.map((section) => {
      const updatedSection = { ...section };
      
      // Update brand name across all sections
      if (updatedSection.content) {
        updatedSection.content = {
          ...updatedSection.content,
          brandName: settings.brandName
        };

        // Update brand logo in guidelines section
        if (section.id === 'guidelines') {
          updatedSection.content.brandLogo = settings.brandLogo;
          updatedSection.content.brandGuideline = `${settings.brandName}/Brand Guideline`;
        }
      }

      return updatedSection;
    });

    dispatch({ type: 'SET_SECTIONS', payload: updatedSections });
    onClose();
  };

  // Apply typography settings
  const applyTypographySettings = () => {
    if (!state.brandData) return;
    // Update brand data with typography settings
    const updatedBrandData = {
      ...state.brandData,
      globalTitleFont: settings.globalTitleFont,
      globalTextFont: settings.globalTextFont,
      globalTitleFontColor: settings.globalTitleFontColor,
      globalTextFontColor: settings.globalTextFontColor
    };

    dispatch({ type: 'SET_BRAND_DATA', payload: updatedBrandData });

    // Update all sections with typography settings
    const updatedSections = state.sections.map((section) => {
      const updatedSection = { ...section };

      // Update section styles with global fonts
      updatedSection.styles = {
        ...updatedSection.styles,
        fontFamily: settings.globalTextFont,
        textColor: settings.globalTextFontColor !== 'transparent' ? settings.globalTextFontColor : updatedSection.styles.textColor
      };

      // Update content styles to use global fonts and colors
      if (updatedSection.content) {
        Object.keys(updatedSection.content).forEach(key => {
          // Update title styles (any key containing 'Title' or 'title')
          if (key.includes('TitleStyles') || key.includes('titleStyles')) {
            updatedSection.content[key] = {
              ...updatedSection.content[key],
              fontFamily: settings.globalTitleFont,
              color: settings.globalTitleFontColor !== 'transparent' ? settings.globalTitleFontColor : updatedSection.content[key]?.color
            };
          }
          // Update general text styles (description, mission, vision, etc.)
          else if (key.includes('Styles') && !key.includes('Title') && !key.includes('title')) {
            updatedSection.content[key] = {
              ...updatedSection.content[key],
              fontFamily: settings.globalTextFont,
              color: settings.globalTextFontColor !== 'transparent' ? settings.globalTextFontColor : updatedSection.content[key]?.color
            };
          }
        });

        // Special handling for specific content elements
        const titleKeys = ['brandBookTitleStyles', 'headerTitleStyles', 'missionTitleStyles', 'visionTitleStyles', 'valuesTitleStyles'];
        const textKeys = ['descriptionStyles', 'missionStyles', 'visionStyles', 'valuesStyles', 'currentDateStyles', 'brandGuidelineStyles', 'designCreditStyles'];

        titleKeys.forEach(key => {
          if (updatedSection.content[key]) {
            updatedSection.content[key] = {
              ...updatedSection.content[key],
              fontFamily: settings.globalTitleFont,
              color: settings.globalTitleFontColor !== 'transparent' ? settings.globalTitleFontColor : updatedSection.content[key].color
            };
          }
        });

        textKeys.forEach(key => {
          if (updatedSection.content[key]) {
            updatedSection.content[key] = {
              ...updatedSection.content[key],
              fontFamily: settings.globalTextFont,
              color: settings.globalTextFontColor !== 'transparent' ? settings.globalTextFontColor : updatedSection.content[key].color
            };
          }
        });
      }

      return updatedSection;
    });

    dispatch({ type: 'SET_SECTIONS', payload: updatedSections });
    onClose();
  };

  // Apply color settings
  const applyColorSettings = () => {
    if (!state.brandData) return;
    // Update brand data with color settings and selected preset
    const updatedBrandData = {
      ...state.brandData,
      colors: {
        ...state.brandData.colors,
        primary: settings.primaryColor,
        secondary: settings.secondaryColor,
        accent: settings.accentColor,
        background: settings.bgColor,
        title: settings.titleColor,
        text: settings.textColor
      },
      selectedColorPreset: settings.selectedColorPreset
    };

    dispatch({ type: 'SET_BRAND_DATA', payload: updatedBrandData });

    // Update sections with color overrides
    const updatedSections = state.sections.map((section) => {
      const updatedSection = { ...section };

      // CRITICAL: Override section background colors based on brand colors
      let sectionBackgroundColor = updatedSection.styles.backgroundColor;
      
      // Apply brand colors to specific sections
      if (section.id === 'guidelines') {
        sectionBackgroundColor = settings.primaryColor;
      } else if (section.id === 'brand-action') {
        sectionBackgroundColor = settings.secondaryColor;
      } else if (section.id === 'thank-you') {
        sectionBackgroundColor = settings.accentColor;
      }

      // Update section styles with background override
      updatedSection.styles = {
        ...updatedSection.styles,
        backgroundColor: sectionBackgroundColor
      };

      // Update customization to reflect the new background color
      if (updatedSection.customization) {
        updatedSection.customization = {
          ...updatedSection.customization,
          backgroundColor: sectionBackgroundColor
        };
      } else {
        // Create customization if it doesn't exist
        updatedSection.customization = {
          backgroundType: 'color',
          backgroundColor: sectionBackgroundColor,
          backgroundImage: '',
          backgroundPosition: 'center',
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
          overlayEnabled: false,
          overlayColor: '#000000',
          overlayOpacity: 50,
          overlayBlendMode: 'normal'
        };
      }

      return updatedSection;
    });

    dispatch({ type: 'SET_SECTIONS', payload: updatedSections });
    onClose();
  };


  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center"
        onClick={onClose}
      >
        {/* Settings Panel - Perfectly Centered */}
        <div
          className="w-[800px] max-w-[90vw] max-h-[85vh] rounded-xl shadow-2xl border backdrop-blur-lg bg-card border-border"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-border">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-workspace-accent rounded-lg flex items-center justify-center">
                <Settings className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-foreground">Brand Settings</h2>
                <p className="text-sm text-muted-foreground">Configure global settings for all sections</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg transition-colors text-muted-foreground hover:text-foreground hover:bg-accent"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(85vh - 180px)' }}>
            
            {/* Tab Navigation */}
            <div className="flex space-x-1 mb-6 p-1 rounded-lg bg-header-control">
              <button
                onClick={() => handleTabChange('general')}
                className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  activeTab === 'general'
                    ? 'bg-background shadow-sm text-foreground-text shadow-sm'
                    : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                }`}
              >
                <Image className="w-4 h-4" />
                <span>General</span>
              </button>

              <button
                onClick={() => handleTabChange('typography')}
                className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  activeTab === 'typography'
                    ? 'bg-background shadow-sm text-foreground-text shadow-sm'
                    : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                }`}
              >
                <Type className="w-4 h-4" />
                <span>Typography</span>
              </button>

              <button
                onClick={() => handleTabChange('colors')}
                className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  activeTab === 'colors'
                    ? 'bg-background shadow-sm text-foreground-text shadow-sm'
                    : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                }`}
              >
                <Palette className="w-4 h-4" />
                <span>Colors</span>
              </button>
            </div>

            {/* Tab Content */}
            <AnimatePresence mode="wait">
              {activeTab === 'general' && (
                <motion.div
                  key="general"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.2 }}
                  className="space-y-4"
                >
                      {/* Brand Name */}
                      <div>
                        <label className="block text-sm font-medium mb-2 text-foreground">Brand Name</label>
                        <input
                          type="text"
                          value={settings.brandName}
                          onChange={(e) => updateSetting('brandName', e.target.value)}
                          placeholder="Enter brand name"
                          className="w-full px-3 py-2 rounded-lg border text-sm bg-background border-input text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-workspace-accent"
                        />
                      </div>

                      {/* Brand Logo and Dark Logo - Side by Side */}
                      <div className="grid grid-cols-2 gap-4">
                        {/* Brand Logo */}
                        <div>
                          <label className="block text-sm font-medium mb-2 text-foreground">Brand Logo</label>
                          <div className="space-y-3">
                            {settings.brandLogo ? (
                              <div className="relative group cursor-pointer" onClick={() => handleImageUpload('brandLogo')}>
                                <img 
                                  src={settings.brandLogo} 
                                  alt="Brand Logo Preview" 
                                  className="w-full h-24 object-cover rounded-lg"
                                  onError={(e) => {
                                    e.currentTarget.style.display = 'none';
                                  }}
                                />
                                {/* Upload overlay on hover */}
                                <div className="absolute inset-0 bg-background/80 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center rounded-lg">
                                  <Upload className="w-6 h-6 text-white" />
                                </div>
                                {/* Remove button */}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    removeBrandLogo();
                                  }}
                                  className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors z-10"
                                  title="Remove logo"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => handleImageUpload('brandLogo')}
                                className="w-full h-24 border-2 border-dashed rounded-lg transition-colors flex items-center justify-center border-border hover:border-workspace-accent bg-muted/50 text-foreground"
                              >
                                <div className="flex items-center space-x-2">
                                  <Upload className="w-5 h-5" />
                                  <span className="text-sm">Upload Logo</span>
                                </div>
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Dark Logo */}
                        <div>
                          <label className="block text-sm font-medium mb-2 text-foreground">Dark Logo</label>
                          <div className="space-y-3">
                            {state.brandData?.primaryDarkLogo ? (
                              <div className="relative group cursor-pointer" onClick={() => handleImageUpload('primaryDarkLogo')}>
                                <img 
                                  src={state.brandData.primaryDarkLogo} 
                                  alt="Dark Logo Preview" 
                                  className="w-full h-24 object-cover rounded-lg"
                                  onError={(e) => {
                                    e.currentTarget.style.display = 'none';
                                  }}
                                />
                                {/* Upload overlay on hover */}
                                <div className="absolute inset-0 bg-background/80 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center rounded-lg">
                                  <Upload className="w-6 h-6 text-white" />
                                </div>
                                {/* Remove button */}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    removeDarkLogo();
                                  }}
                                  className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors z-10"
                                  title="Remove dark logo"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => handleImageUpload('primaryDarkLogo')}
                                className="w-full h-24 border-2 border-dashed rounded-lg transition-colors flex items-center justify-center border-border hover:border-workspace-accent bg-muted/50 text-foreground"
                              >
                                <div className="flex items-center space-x-2">
                                  <Upload className="w-5 h-5" />
                                  <span className="text-sm">Upload Dark Logo</span>
                                </div>
                              </button>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Brand Thumbnail */}
                      <div>
                        <label className="block text-sm font-medium mb-2 text-foreground">Dashboard Thumbnail</label>
                        <div className="space-y-3">
                          {settings.brandThumbnail ? (
                            <div className="relative group cursor-pointer" onClick={() => handleImageUpload('brandThumbnail')}>
                              <img 
                                src={settings.brandThumbnail} 
                                alt="Brand Thumbnail Preview" 
                                className="w-full h-40 object-cover rounded-lg"
                                onError={(e) => {
                                  e.currentTarget.style.display = 'none';
                                }}
                              />
                              {/* Upload overlay on hover */}
                              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center rounded-lg">
                                <Upload className="w-8 h-8 text-white" />
                              </div>
                              {/* Remove button */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeBrandThumbnail();
                                }}
                                className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors z-10"
                                title="Remove thumbnail"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => handleImageUpload('brandThumbnail')}
                              className="w-full h-40 border-2 border-dashed rounded-lg transition-colors flex items-center justify-center border-border hover:border-workspace-accent bg-muted/50 text-foreground"
                            >
                              <div className="flex items-center space-x-2">
                                <Upload className="w-6 h-6" />
                                <span className="text-sm">Upload Thumbnail</span>
                              </div>
                            </button>
                          )}

                          <p className="text-xs text-muted-foreground">
                            This image will be shown in the dashboard's saved brand thumbnails
                          </p>
                        </div>
                      </div>

                      {/* Design Credit */}
                      <div>
                        <label className="block text-sm font-medium mb-2 text-foreground">Design Credit</label>
                        <input
                          type="text"
                          value={settings.designCredit}
                          onChange={(e) => updateSetting('designCredit', e.target.value)}
                          placeholder="e.g. AgencyPlus"
                          className="w-full px-3 py-2 rounded-lg border text-sm bg-background border-input text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-workspace-accent"
                        />
                        <p className="text-xs text-muted-foreground mt-2">
                          This text will replace {'{{designCredit}}'} in your brand guidelines
                        </p>
                      </div>

                      {/* Brand Website URL Section */}
                      <div className="space-y-3">
                        <label className="block text-sm font-medium text-foreground">
                          Brand Website URL
                        </label>
                        
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Globe className="h-4 w-4 text-muted-foreground" />
                          </div>
                          <input
                            type="url"
                            value={state.brandData?.brandUrl || ''}
                            onChange={(e) => {
                              if (!state.brandData) return;
                              dispatch({
                                type: 'SET_BRAND_DATA',
                                payload: {
                                  ...state.brandData,
                                  brandUrl: e.target.value
                                }
                              });
                            }}
                            className="block w-full pl-10 pr-3 py-2 border rounded-md text-sm bg-background border-input text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-workspace-accent"
                            placeholder="https://www.yourbrand.com"
                          />
                        </div>
                        <p className="text-xs text-muted-foreground">
                          This URL will appear in your brand guidelines
                        </p>
                      </div>

                      {/* Save and Reset Buttons for General Tab */}
                      <div className="pt-4 border-t border-border">
                        <div className="flex items-center justify-between">
                          <button
                            onClick={() => {
                              setSettings({
                                brandName: state.brandData?.name || '',
                                brandLogo: '',
                                brandThumbnail: '',
                                globalTitleFont: 'Helvetica',
                                globalTextFont: 'Helvetica',
                                globalTitleFontColor: 'transparent',
                                globalTextFontColor: 'transparent',
                                primaryColor: '#3B82F6',
                                secondaryColor: '#8B5CF6',
                                accentColor: '#F59E0B',
                                bgColor: '#FFFFFF',
                                titleColor: '#000000',
                                textColor: '#374151',
                                selectedColorPreset: null as ColorPreset | null,
                                designCredit: currentWorkspace?.name || ''
                              });
                            }}
                            className="flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors text-muted-foreground hover:text-foreground hover:bg-accent"
                          >
                            <RotateCcw className="w-4 h-4" />
                            <span>Reset</span>
                          </button>
                          
                          <button
                            onClick={applyGeneralSettings}
                            className="flex items-center space-x-2 px-4 py-2 bg-workspace-accent text-white rounded-lg hover:bg-workspace-accent-hover transition-colors"
                          >
                            <Save className="w-4 h-4" />
                            <span>Save</span>
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {activeTab === 'typography' && (
                    <motion.div
                      key="typography"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.2 }}
                      className="space-y-6"
                    >
                      
                      {/* Google Fonts Section */}
                      <div>
                        <div className="flex items-center space-x-2 mb-3">
                          <h4 className="text-sm font-medium text-foreground">Google Fonts</h4>
                          <div className="group relative">
                            <Info className={`w-3 h-3 ${
                              'text-muted-foreground'
                            }`} />
                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap bg-foreground text-background">
                              Choose from 1000+ Google Fonts
                            </div>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-3">
                          {/* Title Font */}
                          <div>
                            <label className="block text-xs font-medium mb-2 text-muted-foreground">Title Font</label>
                            <TypographyDropdown
                              value={settings.globalTitleFont}
                              onChange={(font) => updateSetting('globalTitleFont', font)}
                              isGlobalDarkMode={isGlobalDarkMode}
                              placeholder="Select title font"
                            />
                          </div>

                          {/* Text Font */}
                          <div>
                            <label className="block text-xs font-medium mb-2 text-muted-foreground">Text Font</label>
                            <TypographyDropdown
                              value={settings.globalTextFont}
                              onChange={(font) => updateSetting('globalTextFont', font)}
                              isGlobalDarkMode={isGlobalDarkMode}
                              placeholder="Select text font"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Custom Fonts Section */}
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center space-x-2">
                            <h4 className="text-sm font-medium text-foreground">Custom Fonts</h4>
                            <div className="group relative">
                              <Info className={`w-3 h-3 ${
                                'text-muted-foreground'
                              }`} />
                              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap bg-foreground text-background">
                                Upload .ttf, .otf, .woff, .woff2 files
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex items-center space-x-2">
                            <label className="flex items-center space-x-1 px-3 py-1.5 text-xs bg-workspace-accent text-white rounded-lg hover:bg-workspace-accent-hover transition-colors cursor-pointer">
                              <Upload className="w-3 h-3" />
                              <span>Upload Font</span>
                              <input
                                type="file"
                                accept=".ttf,.otf,.woff,.woff2"
                                onChange={handleCustomFontUpload}
                                className="hidden"
                              />
                            </label>
                          </div>
                        </div>

                        {/* Custom Fonts List */}
                        {customFonts.length > 0 ? (
                          <div className="space-y-2">
                            {customFonts.map((font) => (
                              <div
                                key={font.id}
                                className={`flex items-center justify-between p-3 rounded-lg border ${
                                  'bg-muted/50 border-border'
                                }`}
                              >
                                <div className="flex-1">
                                  <div
                                    className={`font-medium text-sm ${
                                      'text-foreground'
                                    }`}
                                    style={{ fontFamily: font.name }}
                                  >
                                    {font.name}
                                  </div>
                                  <div className="text-xs text-muted-foreground">
                                    {font.format.toUpperCase()} • {Math.round(font.file.size / 1024)}KB
                                  </div>
                                </div>
                                
                                <div className="flex items-center space-x-2">
                                  <button
                                    onClick={() => updateSetting('globalTitleFont', font.name)}
                                    className={`px-2 py-1 text-xs rounded transition-colors ${
                                      settings.globalTitleFont === font.name
                                        ? 'bg-workspace-accent text-white'
                                        : 'bg-header-control text-foreground hover:bg-accent'
                                    }`}
                                  >
                                    Title
                                  </button>
                                  <button
                                    onClick={() => updateSetting('globalTextFont', font.name)}
                                    className={`px-2 py-1 text-xs rounded transition-colors ${
                                      settings.globalTextFont === font.name
                                        ? 'bg-workspace-accent text-white'
                                        : 'bg-header-control text-foreground hover:bg-accent'
                                    }`}
                                  >
                                    Text
                                  </button>
                                  <button
                                    onClick={() => removeCustomFont(font.id)}
                                    className={`p-1 rounded transition-colors ${
                                      isGlobalDarkMode 
                                        ? 'text-red-400 hover:bg-red-900/20' 
                                        : 'text-red-500 hover:bg-red-50'
                                    }`}
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center py-4 text-sm text-muted-foreground">
                            No custom fonts uploaded yet
                          </div>
                        )}
                      </div>

                      {/* Font Colors */}
                      <div className="grid grid-cols-2 gap-4">
                        {/* Title Font Color */}
                        <div>
                          <label className="block text-xs font-medium mb-2 text-muted-foreground">Title Font Color</label>
                          <div className="flex items-center space-x-2">
                            <input
                              type="color"
                              value={settings.globalTitleFontColor === 'transparent' ? '#000000' : settings.globalTitleFontColor}
                              onChange={(e) => updateSetting('globalTitleFontColor', e.target.value)}
                              className="w-8 h-8 rounded cursor-pointer [&::-webkit-color-swatch-wrapper]:bg-transparent [&::-webkit-color-swatch]:border-0 [&::-moz-color-swatch]:border-0"
                            />
                            <input
                              type="text"
                              value={settings.globalTitleFontColor}
                              onChange={(e) => updateSetting('globalTitleFontColor', e.target.value)}
                              className={`flex-1 px-2 py-1 rounded border text-xs ${
                                'bg-background border-input text-foreground'
                              }`}
                              placeholder="transparent"
                            />
                            <button
                              onClick={() => updateSetting('globalTitleFontColor', 'transparent')}
                              className={`px-2 py-1 text-xs rounded border transition-colors ${
                                settings.globalTitleFontColor === 'transparent'
                                  ? 'bg-workspace-accent text-white border-blue-500'
                                  : isGlobalDarkMode 
                                    ? 'bg-gray-800 border-gray-600 text-white hover:bg-gray-700' 
                                    : 'bg-white border-gray-300 text-gray-900 hover:bg-gray-50'
                              }`}
                            >
                              None
                            </button>
                          </div>
                        </div>

                        {/* Text Font Color */}
                        <div>
                          <label className="block text-xs font-medium mb-2 text-muted-foreground">Text Font Color</label>
                          <div className="flex items-center space-x-2">
                            <input
                              type="color"
                              value={settings.globalTextFontColor === 'transparent' ? '#374151' : settings.globalTextFontColor}
                              onChange={(e) => updateSetting('globalTextFontColor', e.target.value)}
                              className="w-8 h-8 rounded cursor-pointer [&::-webkit-color-swatch-wrapper]:bg-transparent [&::-webkit-color-swatch]:border-0 [&::-moz-color-swatch]:border-0"
                            />
                            <input
                              type="text"
                              value={settings.globalTextFontColor}
                              onChange={(e) => updateSetting('globalTextFontColor', e.target.value)}
                              className={`flex-1 px-2 py-1 rounded border text-xs ${
                                'bg-background border-input text-foreground'
                              }`}
                              placeholder="transparent"
                            />
                            <button
                              onClick={() => updateSetting('globalTextFontColor', 'transparent')}
                              className={`px-2 py-1 text-xs rounded border transition-colors ${
                                settings.globalTextFontColor === 'transparent'
                                  ? 'bg-workspace-accent text-white border-blue-500'
                                  : isGlobalDarkMode 
                                    ? 'bg-gray-800 border-gray-600 text-white hover:bg-gray-700' 
                                    : 'bg-white border-gray-300 text-gray-900 hover:bg-gray-50'
                              }`}
                            >
                              None
                            </button>
                          </div>
                        </div>
                      </div>

                      <p className="text-xs text-muted-foreground">
                        These fonts and colors will be applied to all titles and text across all sections. Set to "transparent" or "None" to keep existing colors.
                      </p>

                      {/* Save and Reset Buttons for Typography Tab */}
                      <div className="pt-4 border-t border-border">
                        <div className="flex items-center justify-between">
                          <button
                            onClick={() => {
                              setSettings(prev => ({
                                ...prev,
                                globalTitleFont: 'Helvetica',
                                globalTextFont: 'Helvetica',
                                globalTitleFontColor: 'transparent',
                                globalTextFontColor: 'transparent'
                              }));
                            }}
                            className="flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors text-muted-foreground hover:text-foreground hover:bg-accent"
                          >
                            <RotateCcw className="w-4 h-4" />
                            <span>Reset</span>
                          </button>
                          
                          <button
                            onClick={applyTypographySettings}
                            className="flex items-center space-x-2 px-4 py-2 bg-workspace-accent text-white rounded-lg hover:bg-workspace-accent-hover transition-colors"
                          >
                            <Save className="w-4 h-4" />
                            <span>Save</span>
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {activeTab === 'colors' && (
                    <motion.div
                      key="colors"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.2 }}
                      className="space-y-4"
                    >
                      {/* Select Color Preset */}
                      <div className="space-y-3">
                        <label className="block text-sm font-medium text-foreground">
                          Select a Color Preset
                        </label>
                        {isLoadingColorPresets ? (
                          <LoadingSpinner size="sm" className="py-4" />
                        ) : (
                          <Select
                            value={settings.selectedColorPreset?.id || ''}
                            onValueChange={(value) => {
                              const preset = colorPresets.find(p => p.id === value);
                              if (preset) {
                                setSettings({
                                  ...settings,
                                  selectedColorPreset: preset,
                                  primaryColor: preset.colors[0]?.hex || settings.primaryColor,
                                  secondaryColor: preset.colors[1]?.hex || settings.secondaryColor,
                                  accentColor: preset.colors[2]?.hex || settings.accentColor,
                                  bgColor: preset.colors[3]?.hex || settings.bgColor,
                                  titleColor: preset.colors[4]?.hex || settings.titleColor,
                                  textColor: preset.colors[5]?.hex || settings.textColor
                                });
                              }
                            }}
                          >
                            <SelectTrigger className="bg-background border-input text-foreground">
                              <SelectValue placeholder="Select a color preset" />
                            </SelectTrigger>
                            <SelectContent>
                              {colorPresets.map((preset) => (
                                <SelectItem key={preset.id} value={preset.id}>
                                  {preset.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}

                        {settings.selectedColorPreset && (
                          <div className="flex items-center gap-2 p-4 rounded-lg" style={{
                            backgroundColor: isGlobalDarkMode ? '#1f2937' : '#f9fafb',
                            border: `1px solid ${isGlobalDarkMode ? '#374151' : '#e5e7eb'}`
                          }}>
                            {settings.selectedColorPreset.colors.map((color, i) => (
                              <div
                                key={i}
                                className="w-10 h-10 rounded-full border-2 border-white shadow-md"
                                style={{ backgroundColor: color.hex }}
                                title={color.hex}
                              />
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Assign Colors */}
                      {settings.selectedColorPreset && (
                        <div className="space-y-4">
                          <label className="block text-sm font-medium text-foreground">
                            Assign Colors
                          </label>

                          {/* Row 1: Primary, Secondary, Accent */}
                          <div className="grid grid-cols-3 gap-4">
                            {/* Primary Color */}
                            <div>
                              <label className="block text-xs font-medium mb-2 text-muted-foreground">
                                Primary <span className="text-red-500">*</span>
                              </label>
                              <Select
                                value={settings.primaryColor}
                                onValueChange={(value) => setSettings({ ...settings, primaryColor: value })}
                              >
                                <SelectTrigger className="bg-background border-input text-foreground">
                                  <div className="flex items-center gap-2">
                                    <div className="w-6 h-6 rounded-full border" style={{ backgroundColor: settings.primaryColor }} />
                                    <span className="text-xs">{settings.primaryColor}</span>
                                  </div>
                                </SelectTrigger>
                                <SelectContent>
                                  {settings.selectedColorPreset.colors.map((color, i) => (
                                    <SelectItem key={i} value={color.hex}>
                                      <div className="flex items-center gap-2">
                                        <div className="w-6 h-6 rounded-full border" style={{ backgroundColor: color.hex }} />
                                        <span className="text-xs">{color.hex}</span>
                                      </div>
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>

                            {/* Secondary Color */}
                            <div>
                              <label className="block text-xs font-medium mb-2 text-muted-foreground">
                                Secondary
                              </label>
                              <Select
                                value={settings.secondaryColor || 'none'}
                                onValueChange={(value) => setSettings({ ...settings, secondaryColor: value === 'none' ? '' : value })}
                              >
                                <SelectTrigger className="bg-background border-input text-foreground">
                                  <div className="flex items-center gap-2">
                                    {settings.secondaryColor ? (
                                      <>
                                        <div className="w-6 h-6 rounded-full border" style={{ backgroundColor: settings.secondaryColor }} />
                                        <span className="text-xs">{settings.secondaryColor}</span>
                                      </>
                                    ) : (
                                      <span className="text-xs">None</span>
                                    )}
                                  </div>
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">
                                    <span className="text-xs">None</span>
                                  </SelectItem>
                                  {settings.selectedColorPreset.colors.map((color, i) => (
                                    <SelectItem key={i} value={color.hex}>
                                      <div className="flex items-center gap-2">
                                        <div className="w-6 h-6 rounded-full border" style={{ backgroundColor: color.hex }} />
                                        <span className="text-xs">{color.hex}</span>
                                      </div>
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>

                            {/* Accent Color */}
                            <div>
                              <label className="block text-xs font-medium mb-2 text-muted-foreground">
                                Accent
                              </label>
                              <Select
                                value={settings.accentColor || 'none'}
                                onValueChange={(value) => setSettings({ ...settings, accentColor: value === 'none' ? '' : value })}
                              >
                                <SelectTrigger className="bg-background border-input text-foreground">
                                  <div className="flex items-center gap-2">
                                    {settings.accentColor ? (
                                      <>
                                        <div className="w-6 h-6 rounded-full border" style={{ backgroundColor: settings.accentColor }} />
                                        <span className="text-xs">{settings.accentColor}</span>
                                      </>
                                    ) : (
                                      <span className="text-xs">None</span>
                                    )}
                                  </div>
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">
                                    <span className="text-xs">None</span>
                                  </SelectItem>
                                  {settings.selectedColorPreset.colors.map((color, i) => (
                                    <SelectItem key={i} value={color.hex}>
                                      <div className="flex items-center gap-2">
                                        <div className="w-6 h-6 rounded-full border" style={{ backgroundColor: color.hex }} />
                                        <span className="text-xs">{color.hex}</span>
                                      </div>
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>

                          {/* Row 2: Background, Title, Text */}
                          <div className="grid grid-cols-3 gap-4">
                            {/* Background Color */}
                            <div>
                              <label className="block text-xs font-medium mb-2 text-muted-foreground">
                                Background <span className="text-red-500">*</span>
                              </label>
                              <Select
                                value={settings.bgColor}
                                onValueChange={(value) => setSettings({ ...settings, bgColor: value })}
                              >
                                <SelectTrigger className="bg-background border-input text-foreground">
                                  <div className="flex items-center gap-2">
                                    <div className="w-6 h-6 rounded-full border" style={{ backgroundColor: settings.bgColor }} />
                                    <span className="text-xs">{settings.bgColor}</span>
                                  </div>
                                </SelectTrigger>
                                <SelectContent>
                                  {settings.selectedColorPreset.colors.map((color, i) => (
                                    <SelectItem key={i} value={color.hex}>
                                      <div className="flex items-center gap-2">
                                        <div className="w-6 h-6 rounded-full border" style={{ backgroundColor: color.hex }} />
                                        <span className="text-xs">{color.hex}</span>
                                      </div>
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>

                            {/* Title Color */}
                            <div>
                              <label className="block text-xs font-medium mb-2 text-muted-foreground">
                                Title <span className="text-red-500">*</span>
                              </label>
                              <Select
                                value={settings.titleColor}
                                onValueChange={(value) => setSettings({ ...settings, titleColor: value })}
                              >
                                <SelectTrigger className="bg-background border-input text-foreground">
                                  <div className="flex items-center gap-2">
                                    <div className="w-6 h-6 rounded-full border" style={{ backgroundColor: settings.titleColor }} />
                                    <span className="text-xs">{settings.titleColor}</span>
                                  </div>
                                </SelectTrigger>
                                <SelectContent>
                                  {settings.selectedColorPreset.colors.map((color, i) => (
                                    <SelectItem key={i} value={color.hex}>
                                      <div className="flex items-center gap-2">
                                        <div className="w-6 h-6 rounded-full border" style={{ backgroundColor: color.hex }} />
                                        <span className="text-xs">{color.hex}</span>
                                      </div>
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>

                            {/* Text Color */}
                            <div>
                              <label className="block text-xs font-medium mb-2 text-muted-foreground">
                                Text
                              </label>
                              <Select
                                value={settings.textColor || 'none'}
                                onValueChange={(value) => setSettings({ ...settings, textColor: value === 'none' ? '' : value })}
                              >
                                <SelectTrigger className="bg-background border-input text-foreground">
                                  <div className="flex items-center gap-2">
                                    {settings.textColor ? (
                                      <>
                                        <div className="w-6 h-6 rounded-full border" style={{ backgroundColor: settings.textColor }} />
                                        <span className="text-xs">{settings.textColor}</span>
                                      </>
                                    ) : (
                                      <span className="text-xs">None</span>
                                    )}
                                  </div>
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">
                                    <span className="text-xs">None</span>
                                  </SelectItem>
                                  {settings.selectedColorPreset.colors.map((color, i) => (
                                    <SelectItem key={i} value={color.hex}>
                                      <div className="flex items-center gap-2">
                                        <div className="w-6 h-6 rounded-full border" style={{ backgroundColor: color.hex }} />
                                        <span className="text-xs">{color.hex}</span>
                                      </div>
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Background Override Notice */}
                      <div className={`p-3 rounded-lg border ${
                        isGlobalDarkMode
                          ? 'bg-blue-900/20 border-blue-700/50 text-blue-200'
                          : 'bg-blue-50 border-blue-200 text-blue-800'
                      }`}>
                        <p className="text-xs">
                          <strong>Note:</strong> These colors will override section background colors. Primary color applies to Guidelines, Secondary to Brand Action, and Accent to Thank You sections.
                        </p>
                      </div>

                      {/* Save and Reset Buttons for Colors Tab */}
                      <div className="pt-4 border-t border-border">
                        <div className="flex items-center justify-between">
                          <button
                            onClick={() => {
                              setSettings(prev => ({
                                ...prev,
                                primaryColor: '#3B82F6',
                                secondaryColor: '#8B5CF6',
                                accentColor: '#F59E0B',
                                bgColor: '#FFFFFF',
                                titleColor: '#000000',
                                textColor: '#374151',
                                selectedColorPreset: null
                              }));
                            }}
                            className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                              isGlobalDarkMode
                                ? 'text-gray-400 hover:text-white hover:bg-gray-800'
                                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                            }`}
                          >
                            <RotateCcw className="w-4 h-4" />
                            <span>Reset</span>
                          </button>

                          <button
                            onClick={applyColorSettings}
                            className="flex items-center space-x-2 px-4 py-2 bg-workspace-accent text-white rounded-lg hover:bg-workspace-accent-hover transition-colors"
                          >
                            <Save className="w-4 h-4" />
                            <span>Save</span>
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>


        </div>
      </div>

      {/* Image Upload Modal */}
      <ImageUploadModal
        isOpen={showImageModal}
        onClose={() => {
          setShowImageModal(false);
          setCurrentImageField(null);
        }}
        onImageSelect={handleImageSelect}
        isGlobalDarkMode={isGlobalDarkMode}
        title={
          currentImageField === 'brandLogo' ? 'Upload Brand Logo' :
          currentImageField === 'brandThumbnail' ? 'Upload Brand Thumbnail' :
          'Upload Image'
        }
        acceptedFormats={['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg']}
        maxFileSize={10}
        showUnsplash={true}
        initialSearchQuery={
          currentImageField === 'brandLogo' ? 'logo design' :
          currentImageField === 'brandThumbnail' ? 'brand identity' :
          ''
        }
      />
    </>
  );
};